import React, { useEffect, useState } from 'react';
import { ArrowRight, ChevronDown, Facebook, Instagram, Linkedin, MessageCircle } from 'lucide-react';
import { AppleIcon, GooglePlayIcon } from './StoreIcons';
import { useScrollReveal } from '../lib/useScrollReveal';
import { APP_STORES } from '../config/appStores';

interface FooterProps { onNavigate?: (page: string) => void; }

// Copy do footer da pagina do evento (unique-site newFooterV2.php). "Criar meu evento"
// e o bloco do app sao escolhas da home (2026-09-15).
const quickLinks = [
  { label: 'Eventos', page: 'home', href: '/#todos-os-eventos' },
  { label: 'Criar meu evento', page: 'produtor', href: '/produtor' },
  { label: 'Login', page: 'login', href: 'https://uniqueingressos.com.br/login' },
  { label: 'Ajuda', page: 'ajuda', href: '/ajuda' },
];
const legalLinks = [
  { label: 'Política de Privacidade', page: 'politica-privacidade', href: '/politica-privacidade' },
  { label: 'Política de Cookies', page: 'politica-cookies', href: '/politica-cookies' },
];
const socialLinks = [
  { label: 'Instagram', href: 'https://www.instagram.com/uniqueingressos/', Icon: Instagram },
  { label: 'Facebook', href: 'https://www.facebook.com/uniqueingressos', Icon: Facebook },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/unique-ingressos/', Icon: Linkedin },
];
const TERMS_URL = 'https://uniqueingressos.com.br/uploads/termo_uso/termos_e_condicoes.pdf';
const WHATSAPP_URL = 'https://api.whatsapp.com/send?l=pt&phone=5582996064988';

function FooterGroup({ title, compact, children }: { title: string; compact: boolean; children: React.ReactNode }) {
  const [expanded, setExpanded] = useState(false);
  const reveal = useScrollReveal<HTMLDetailsElement>();
  return <details {...reveal} className="unique-footer__group" open={!compact || expanded}
    onToggle={(event) => { if (compact) setExpanded(event.currentTarget.open); }}>
    <summary tabIndex={compact ? 0 : -1} onClick={(event) => { if (!compact) event.preventDefault(); }}>
      <h2 className="unique-footer__title">{title}</h2><ChevronDown size={18} aria-hidden="true" />
    </summary>
    <ul className="unique-footer__links">{children}</ul>
  </details>;
}

function FooterLink({ label, page, href, onNavigate }: { label: string; page: string; href: string; onNavigate?: (page: string) => void }) {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!onNavigate) return;
    event.preventDefault();
    const catalog = page === 'home' ? document.getElementById('todos-os-eventos') : null;
    if (catalog) catalog.scrollIntoView({ behavior: 'smooth' });
    else onNavigate(page);
  };
  return <li><a href={href} onClick={handleClick}><ArrowRight size={14} aria-hidden="true" /><span>{label}</span></a></li>;
}

export function Footer({ onNavigate }: FooterProps) {
  const [compact, setCompact] = useState(() => window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)');
    const update = () => setCompact(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  const brandReveal = useScrollReveal<HTMLDivElement>();
  const storesReveal = useScrollReveal<HTMLDivElement>();
  const contactReveal = useScrollReveal<HTMLDivElement>();
  const bottomReveal = useScrollReveal<HTMLDivElement>();
  return (
    <footer className="unique-footer">
      <div className="unique-footer__glow" aria-hidden="true" />
      <div className="unique-container">
        <div className="unique-footer__top">
          <div {...brandReveal} className="unique-footer__brand">
            <img className="unique-footer__logo" src="https://app.uniqueingressos.com.br/images/unique-logo.svg" alt="Unique Ingressos" width="132" height="44" loading="lazy" />
            <p className="unique-footer__name">Unique <span>Ingressos</span></p>
            <p className="unique-footer__tagline">Tecnologia que impulsiona as maiores produtoras</p>
            <p className="unique-footer__description">Mais do que uma plataforma de venda de ingressos, somos a solução inteligente para grandes eventos.</p>
          </div>
          <FooterGroup title="Links Rápidos" compact={compact}>
            {quickLinks.map((link) => <FooterLink key={link.page} {...link} onNavigate={onNavigate} />)}
          </FooterGroup>
          <FooterGroup title="Políticas & Legal" compact={compact}>
            <li><a href={TERMS_URL} target="_blank" rel="noopener noreferrer" aria-label="Termos de Uso, PDF (abre em nova aba)"><ArrowRight size={14} aria-hidden="true" /><span>Termos de Uso</span></a></li>
            {legalLinks.map((link) => <FooterLink key={link.page} {...link} onNavigate={onNavigate} />)}
          </FooterGroup>
          <div {...storesReveal} className="unique-footer__app">
            <h2 className="unique-footer__title">Baixe o app</h2>
            <p>Seus ingressos sempre com você.</p>
            <div className="unique-footer__stores">
              <a href={APP_STORES.appStore} target="_blank" rel="noopener noreferrer"><AppleIcon size={20} /> App Store</a>
              <a href={APP_STORES.googlePlay} target="_blank" rel="noopener noreferrer"><GooglePlayIcon size={20} /> Google Play</a>
            </div>
          </div>
        </div>
        <div {...contactReveal} className="unique-footer__contact">
          <div className="unique-footer__community">
            <h2 className="unique-footer__title">Siga-nos nas Redes</h2>
            <p>Acompanhe nossas novidades, eventos exclusivos e promoções especiais.</p>
            <div className="unique-footer__social">
              {socialLinks.map(({ label, href, Icon }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={`${label} da Unique (abre em nova aba)`}><Icon size={18} aria-hidden="true" /></a>
              ))}
            </div>
          </div>
          <div className="unique-footer__support">
            <h2 className="unique-footer__title">Suporte Direto</h2>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="unique-footer__whatsapp">
              <MessageCircle size={20} aria-hidden="true" /> Chamar no WhatsApp
            </a>
          </div>
        </div>
        <div {...bottomReveal} className="unique-footer__bottom">
          <p>© {new Date().getFullYear()} Unique Ingressos. Todos os direitos reservados.</p>
          <p>CNPJ: 39.776.762/0001-10 • Unique Pay Meios de Pagamentos LTDA</p>
        </div>
      </div>
    </footer>
  );
}
